import {Component, Input, ElementRef, ViewChild, AfterViewInit, HostListener, Output, EventEmitter, OnDestroy} from '@angular/core';
import { Chart, registerables } from 'chart.js';
import * as _ from 'lodash';
import { NgClass, NgStyle, NgFor } from '@angular/common';

Chart.register(...registerables);

@Component({
  selector: 'app-score-drilldown-bar-chart',
  templateUrl: './score-drilldown-bar-chart.component.html',
  styleUrls: ['./score-drilldown-bar-chart.component.scss'],
  imports: [NgClass, NgStyle, NgFor]
})
export class ScoreDrilldownBarChartComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  @Output() onGraphDataPointClick: any = new EventEmitter<any>();
  private chartInstance1: Chart | null = null;
  private chartInstance2: Chart | null = null;
  private resizeTimeout: any;
  isCreatingCharts: boolean = false;

  selectedDatasetIndex: number | null = null;
  
  public uniqueId1: string = 'myChart-' + Math.random().toString(36).substring(2, 9);
  public uniqueId2: string = 'myChart2-' + Math.random().toString(36).substring(2, 9);

  tooltipX: any = '';
  tooltipY: any = '';
  tooltipVisible: any = '';

  @Input('chartData')
  set chartData(value) {
    this.selectedDatasetIndex = null;
    this.data = { ...value }; 
    this.labels = value?.labels;
    this.ratingName = value?.ratingName;
    this.rating = value?.rating;
    this.responses = value?.responses;
    this.dataDeepCopy = { ...value };   
    this.updateChartContainerHeight();
    if (this.chartInstance1 && this.chartInstance2) {
      this.createCharts();
    }
  }

  data: any = {};
  dataDeepCopy: any = {};
  labels: any = [];
  event: any = {};
  previousTime: any = {};
  ratingName: any = '';
  rating: any = [];
  responses: any = [];

  /** Chart height in px; increases when x-axis labels wrap to more than 3 lines */
  chartContainerHeight: number = 280;
  private readonly LABEL_LINE_HEIGHT = 18;
  private readonly MAX_CHARS_PER_LINE = 16;
  private readonly BASE_CHART_HEIGHT = 280;
  private readonly BASE_LABEL_PADDING = 130;

  ngAfterViewInit(): void {
    this.createCharts();
  }

  ngOnDestroy(): void {
    if (this.chartInstance1) {
      this.chartInstance1.destroy();
      this.chartInstance1 = null;
    }
    if (this.chartInstance2) {
      this.chartInstance2.destroy();
      this.chartInstance2 = null;
    }
    clearTimeout(this.resizeTimeout);
  }

  @HostListener('window:resize')
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.createCharts();
    }, 200);
  }

  calcWidth(): any {
    const minWidth = '100%';
    const calculatedWidth =
      (this.data.labels ? this.data.labels.length : 0) * 142.8 + 'px';
    return `max(${minWidth}, ${calculatedWidth})`;
  }

  /** Returns the maximum number of lines any label uses when wrapped at MAX_CHARS_PER_LINE (same as customLabelsPlugin). */
  getMaxLabelLineCount(): number {
    const labels = this.labels || this.data?.labels || [];
    if (!labels.length) return 1;
    let maxLines = 1;
    const maxCharsPerLine = this.MAX_CHARS_PER_LINE;
    labels.forEach((label: string) => {
      let words = String(label).split(' ');
      if(!String(label).includes(' ')) {
        words = String(label).match(new RegExp(`.{1,${maxCharsPerLine}}`, "g")) || [];
      }
      let lineCount = 0;
      let currentLine = '';
      words.forEach((word: string) => {
        if ((currentLine + word).length > maxCharsPerLine) {
          lineCount++;
          currentLine = word + ' ';
        } else {
          currentLine += word + ' ';
        }
      });
      if (currentLine.trim()) lineCount++;
      if (lineCount > maxLines) maxLines = lineCount;
    });
    return maxLines;
  }

  /** Updates chartContainerHeight when x-axis labels wrap to more than 3 lines. */
  updateChartContainerHeight(): void {
    const maxLines = this.getMaxLabelLineCount();
    if (maxLines > 3) {
      const extra = (maxLines - 3) * this.LABEL_LINE_HEIGHT;
      this.chartContainerHeight = this.BASE_CHART_HEIGHT + extra;
    } else {
      this.chartContainerHeight = this.BASE_CHART_HEIGHT;
    }
  }

  setActiveBar(index) {
    const chartDataset = _.cloneDeep(this.data?.datasets);
    chartDataset.forEach(item => {
      const baseColor = item?.backgroundColor;
      const dataLength = item.data.length;
      const backgroundColorArray: any = [];
      for (let i = 0; i < dataLength; i++) {
        if (i === index) {
          const semiTransparentColor = baseColor;
          backgroundColorArray.push(semiTransparentColor);
        } else {
          backgroundColorArray.push(baseColor + '80');
        }
      }
      item.backgroundColor = backgroundColorArray;
    });
    this.dataDeepCopy.datasets = chartDataset;
    this.chartInstance1?.update();
    this.selectedDatasetIndex = null;
  }
  
  createCharts(): void {
    if (this.isCreatingCharts) return;
    this.isCreatingCharts = true;
    if (this.chartInstance1) {
      this.chartInstance1.destroy();
      this.chartInstance1 = null;
    }
    if (this.chartInstance2) {
      this.chartInstance2.destroy();
      this.chartInstance2 = null;
    }
    this.updateChartContainerHeight();
    const maxLabelLines = this.getMaxLabelLineCount();
    const extraPadding = maxLabelLines > 3 ? (maxLabelLines - 3) * this.LABEL_LINE_HEIGHT : 0;
    let paddingBottom = this.BASE_LABEL_PADDING + extraPadding;
    let paddingBottom2 = this.BASE_LABEL_PADDING + extraPadding;
    const div = document.getElementById('box');
    if (div) {
      const isScrollable = div.scrollWidth > div.clientWidth;
      if (isScrollable) {
        paddingBottom2 = 140 + extraPadding;
      }
    }

    const customLabelsPlugin = {
      id: 'customLabels',
      afterDraw: (chart, args, options) => {
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        const chartArea = chart.chartArea;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.font = '14px Asap';
        ctx.fillStyle = chart.canvas.id.includes('myChart2') ? '#FFFFFF' : '#000000';
        const labelY = chartArea.bottom + 10;
        chart.data.labels.forEach((label, i) => {
          const x = xAxis.getPixelForValue(i);
          const maxCharsPerLine = 16;
          let words = String(label).split(' ');
          if(!String(label).includes(' ')) {
            words = String(label).match(new RegExp(`.{1,${maxCharsPerLine}}`, "g")) || [];
          }
          const lines: any = [];
          let currentLine = '';
          words.forEach((word) => {
            if ((currentLine + word).length > maxCharsPerLine) {
              lines.push(currentLine.trim());
              currentLine = word + ' ';
            } else {
              currentLine += word + ' ';
            }
          });
          if (currentLine) {
            lines.push(currentLine.trim());
          }
          lines.forEach((line, lineIndex) => {
            ctx.fillText(line, x, labelY + lineIndex * 18);
          });
        });
        ctx.restore();
      },
    };
    const config: any = {
      type: 'bar',
      data: this.dataDeepCopy,
      plugins: [customLabelsPlugin],
      options: {
        maintainAspectRatio: false,
        responsive: true,
        layout: {
          padding: { bottom: paddingBottom, top: 12, left: 0, right: 0 },
        },
        scales: {
          x: {
            stacked: true,
            ticks: {
              display: false,
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
            },
            grid: {
              drawTicks: false,
              display: false,
              lineWidth: 0,
              drawOnChartArea: false,
              color: 'rgba(255, 255, 255, 0)',
            },
            border: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            stacked: true,
            ticks: {
              display: false,
              stepSize: 20,
              maxTicksLimit: 6,
            },
            grid: {
              drawTicks: false,
              drawBorder: false,
              display: true,
              color: 'rgba(0, 0, 0, 0.3)',
              lineWidth: 0.5, 
            },
            border: {
              display: false, 
            },
            min: 0,
            max: 100,
          },
        },
        onClick: (event) => {
          if (this.chartInstance1) {
            const activePoints = this.chartInstance1.getElementsAtEventForMode(
              event,
              'nearest',
              { intersect: true },
              false
            );
            if (activePoints.length) {
              const clickedIndex = activePoints[0].index; 
              const datasetIndex = activePoints[0].datasetIndex;
              this.setActiveBar(clickedIndex); 
              const barChartPointData = {
                data: this.dataDeepCopy,
                clickedIndex: clickedIndex,
                datasetIndex: datasetIndex,
              };
              this.onGraphDataPointClick.emit(barChartPointData);
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
          },
        },
        hover: {
          mode: null
        },
      },
    };
    const config2: any = {
      type: 'bar',
      data: this.dataDeepCopy,
      plugins: [customLabelsPlugin],
      options: {
        maintainAspectRatio: false,
        responsive: true,
        layout: {
          padding: { bottom: paddingBottom2, top: 0, left: 0, right: 0 },
        },
        scales: {
          x: {
            stacked: true,
            ticks: {
              display: false,
              maxRotation: 0,
              minRotation: 0,
            },
            grid: {
              drawTicks: false,
              drawBorder: false,
              display: false,
            },
            border: {
              display: false,
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            afterFit: (ctx: any) => {
              ctx.width = 55;
            },
            grid: {
              drawTicks: false,
              drawBorder: false,
              display: true,
              color: 'rgba(0, 0, 0, 0.8)',
              lineWidth: 0.5,
            },
            border: {
              display: false,
            },
            ticks: {
              stepSize: 20,
              maxTicksLimit: 6,
              font: {
                size: 14,
                family: 'Asap',
              },
              callback: (value: string | number) => {
                const numericValue =
                  typeof value === 'number' ? value : Number(value);
                return numericValue + '%';
              },
              padding: 5,
            },
            title: {
              display: true,
              font: {
                size: 14,
                family: 'Asap',
              },
              text: '% Total responses',
              padding: {
                top: 0,
                bottom: 40,
              },
            },
            min: 0,
            max: 100,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        hover: {
          mode: null
        },
      },
    };
    
    setTimeout(() => {
      try {
        this.chartInstance1 = new Chart(this.uniqueId1, config);
        this.chartInstance2 = new Chart(this.uniqueId2, config2);
        const canvas = document.getElementById(this.uniqueId1) as HTMLCanvasElement;
        if (canvas) {
          canvas.addEventListener('mousemove', (event) => {
            const points = this.chartInstance1?.getElementsAtEventForMode(
              event,
              'nearest',
              { intersect: true },
              false
            );
            canvas.style.cursor = points?.length ? 'pointer' : 'default';
          });
        }
        this.chartInstance1.canvas.addEventListener('mousemove', (event) => {
          if(this.chartInstance1) {
            const chartArea = this.chartInstance1.chartArea;
            const mouseX = event.offsetX;
            const mouseY = event.offsetY;
            if (mouseX >= chartArea.left && mouseX <= chartArea.right && mouseY >= chartArea.top && mouseY <= chartArea.bottom) {
              const nearestIndex = Math.floor((mouseX - chartArea.left) / (chartArea.right - chartArea.left) * this.labels.length);
              if (nearestIndex >= 0 && nearestIndex < this.labels.length) {
                const visibleDataPoints = this.data.datasets
                  .map((dataset, datasetIndex) => {
                    if(this.chartInstance1) {
                      const meta = this.chartInstance1.getDatasetMeta(datasetIndex);
                      return meta.hidden ? null : { label: dataset.label, value: dataset.data[nearestIndex] };
                    } return;
                  })
                  .filter(dataPoint => dataPoint !== null);
                const obj = {
                  index: nearestIndex,
                  time: new Date().getTime(),
                  label: this.labels[nearestIndex],
                  response: this.responses[nearestIndex],
                  dataPoints: visibleDataPoints,
                  ratingName: this.ratingName,
                  rating: this.rating[nearestIndex]
                };
                this.event = obj;
                this.tooltipVisible = 'block';
              }
            } else {
              this.tooltipVisible = 'none';
            }
          }
        });
      } catch (error) {
        console.error('Error creating charts:', error);
      } finally {
        this.isCreatingCharts = false;
      }
    }, 100);
  }

  toggleDatasetVisibility(index: number) {
    if(this.data.datasets.length === 1) return;
    if (this.selectedDatasetIndex === index) {
      this.selectedDatasetIndex = null;
    } else {
      this.selectedDatasetIndex = index;
    }
    const chart = this.chartInstance1;
    if (!chart) return;
    const datasetMeta = chart.getDatasetMeta(index);
    const isVisible = datasetMeta.hidden === null || datasetMeta.hidden === false;
    const anyHidden = chart.data.datasets.some((_, i) => {
      const meta = chart.getDatasetMeta(i);
      return meta.hidden === true;
    });
    if (isVisible && anyHidden) {
      chart.data.datasets.forEach((_, i) => {
        const meta = chart.getDatasetMeta(i);
        if (i !== index) {
          meta.hidden = false;
        }
      });
    } else {
      chart.data.datasets.forEach((_, i) => {
        const meta = chart.getDatasetMeta(i);
        meta.hidden = true;
      });
      datasetMeta.hidden = false;
    }
    chart.update();
  }
  
  isAllVisible() {
    if (!this.chartInstance1) return false;
    return this.chartInstance1.data.datasets.every((_, i) => {
      const meta = this.chartInstance1!.getDatasetMeta(i);
      return meta.hidden === false || meta.hidden === null;
    });
  }
  
  isAnyVisible() {
    if (!this.chartInstance1) return false;
    return this.chartInstance1.data.datasets.some((_, i) => {
      const meta = this.chartInstance1!.getDatasetMeta(i);
      return meta.hidden === false || meta.hidden === null;
    });
  }
  
  isSelected(index: number) {
    if (!this.chartInstance1) return false;
    const meta = this.chartInstance1.getDatasetMeta(index);
    return meta.hidden === false || meta.hidden === null;
  }
  
  isNoneSelected() {
    if (!this.chartInstance1) return false;
    return !this.chartInstance1.data.datasets.some((_, i) => {
      const meta = this.chartInstance1!.getDatasetMeta(i);
      return meta.hidden === true;
    });
  }
  
  @HostListener('document:mousemove', ['$event'])
    onMouseMove(event: MouseEvent): void {
      const containerEl = this.chartContainer?.nativeElement;
      if (!containerEl) return;
      const rect = containerEl.getBoundingClientRect();
      const relativeX = event.clientX - rect.left;
      const relativeY = event.clientY - rect.top;
      this.tooltipX = relativeX >= rect.width / 2 ? relativeX - 230 : relativeX + 15;
      this.tooltipY = relativeY + 15;
      if (this.previousTime !== this.event?.time && this.event?.time) {
        this.previousTime = this.event?.time;
        this.tooltipVisible = 'block';
      } else {
        this.tooltipVisible = 'none';
      }
    }
}